import { useCallback, useEffect, useRef, useState } from 'react'
import { replace as replaceRoute, useRouter } from '@/lib/router'

import type {
  Account,
  AnnouncementUnreadCountResponse,
  ConsoleAuthStatus,
  ConsoleUser,
  LicenseRuntimeStatus,
  LocalWechatAccount,
} from '@/types'
import { menuItemsFor } from '@/lib/constants'
import {
  avatarFor,
  clearStoredConsoleUserData,
  readStoredAccount,
  saveStoredAccount,
  setAccountStorageScope,
} from '@/lib/utils'

import { ToastProvider } from '@/components/Toast'
import { StandardDialogProvider } from '@/components/StandardDialog'
import { PrimaryRail } from '@/components/shell/PrimaryRail'
import { SecondaryColumn } from '@/components/shell/SecondaryColumn'
import { MainPane } from '@/components/shell/MainPane'
import { ChatList } from '@/components/conversation/ChatList'
import { ViewRouter } from '@/components/shell/ViewRouter'
import { apiGet, apiPost, CONSOLE_SESSION_EXPIRED_EVENT, SESSION_EXPIRED_EVENT } from '@/lib/api'

import { useAccountProfile } from '@/hooks/useAccountProfile'
import { useLogin } from '@/hooks/useLogin'
import { useContactsSync } from '@/hooks/useContactsSync'
import { useLocalWechatAccounts } from '@/hooks/useLocalWechatAccounts'
import { useBotData } from '@/hooks/useBotData'
import { useRouteContext } from '@/hooks/useRouteContext'
import { useLabels } from '@/hooks/useLabels'
import { useMessageNotifications } from '@/hooks/useMessageNotifications'
import { ConsoleAuthGate } from '@/components/ConsoleAuthGate'

const SELECTED_WXID_SAVE_DELAY_MS = 10 * 60 * 1000

export default function App() {
  const [consoleUser, setConsoleUser] = useState<ConsoleUser | null>(null)
  const [consoleBootstrapped, setConsoleBootstrapped] = useState(true)
  const [authLoading, setAuthLoading] = useState(true)

  function handleConsoleAuthenticated(user: ConsoleUser) {
    setConsoleUser(user)
    replaceRoute('/dashboard')
  }

  // Any API call answered with 401 (console cookie expired/revoked) drops the
  // shell back to the login screen. The api layer throttles the event.
  useEffect(() => {
    const onConsoleSessionExpired = () => setConsoleUser(null)
    window.addEventListener(CONSOLE_SESSION_EXPIRED_EVENT, onConsoleSessionExpired)
    return () => window.removeEventListener(CONSOLE_SESSION_EXPIRED_EVENT, onConsoleSessionExpired)
  }, [])

  useEffect(() => {
    let cancelled = false
    apiGet<ConsoleAuthStatus>('/auth/status', { silent: true })
      .then((status) => {
        if (cancelled) return
        setConsoleUser(status.user)
        setConsoleBootstrapped(status.bootstrapped)
      })
      .catch(() => {
        if (!cancelled) setConsoleUser(null)
      })
      .finally(() => {
        if (!cancelled) setAuthLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  return (
    <ToastProvider>
      <StandardDialogProvider>
        {authLoading ? (
          <div className="h-screen w-screen bg-page flex items-center justify-center text-muted">
            正在检查控制台登录状态...
          </div>
        ) : !consoleUser ? (
          <ConsoleAuthGate bootstrapped={consoleBootstrapped} onAuthenticated={handleConsoleAuthenticated} />
        ) : (
          <AuthenticatedApp consoleUser={consoleUser} onConsoleUserChange={setConsoleUser} />
        )}
      </StandardDialogProvider>
    </ToastProvider>
  )
}

function AuthenticatedApp({
  consoleUser,
  onConsoleUserChange,
}: {
  consoleUser: ConsoleUser
  onConsoleUserChange: (user: ConsoleUser | null) => void
}) {
  const router = useRouter()
  const isAdmin = consoleUser.role === 'admin'
  const [licenseStatus, setLicenseStatus] = useState<LicenseRuntimeStatus | null>(null)
  const [announcementUnreadCount, setAnnouncementUnreadCount] = useState(0)
  const [consoleLogoutConfirmOpen, setConsoleLogoutConfirmOpen] = useState(false)
  const [consoleLogoutLoading, setConsoleLogoutLoading] = useState(false)
  // Scope local account storage to this console user before any reads/writes.
  setAccountStorageScope(consoleUser.uuid)
  // Restore local selection only after /accounts confirms ownership.
  const [account, setAccount] = useState<Account | null>(null)
  const storedAccountCandidateRef = useRef<Account | null>(
    typeof window !== 'undefined' ? readStoredAccount() : null,
  )
  const pendingSelectedWxidRef = useRef<string | null>(null)
  const selectedWxidSaveTimerRef = useRef<number | null>(null)
  const restoredRemoteSelectionRef = useRef(false)
  const lastObservedAccountWxidRef = useRef(account?.wxid || '')
  const skipNextAccountSaveRef = useRef(false)

  const {
    localAccounts,
    localAccountsLoaded,
    localAccountsError,
    refreshLocalAccounts,
    assertOwnWechatAccount,
    onlineWxAccount,
    downlineWxAccount,
    removeWxAccount,
  } = useLocalWechatAccounts({
    consoleUserUuid: consoleUser.uuid,
    currentWxid: account?.wxid || '',
    onCurrentAccountCleared: () => clearLocalSession(),
    onSessionExpired: () => startAddWechatAccount(),
  })

  const refreshLicenseStatus = useCallback(async () => {
    if (!isAdmin) {
      setLicenseStatus(null)
      return
    }
    try {
      const status = await apiGet<LicenseRuntimeStatus>('/license/status', { silent: true })
      setLicenseStatus(status)
    } catch (err) {
      setLicenseStatus({
        success: false,
        mode: 'gateway',
        message: err instanceof Error ? err.message : String(err),
      })
    }
  }, [isAdmin])

  const clearPendingSelectedWxidSave = useCallback(() => {
    if (selectedWxidSaveTimerRef.current) {
      window.clearTimeout(selectedWxidSaveTimerRef.current)
      selectedWxidSaveTimerRef.current = null
    }
    pendingSelectedWxidRef.current = null
  }, [])

  const saveSelectedWxidNow = useCallback(async (wxid: string) => {
    await apiPost('/auth/preferences', { lastSelectedWxid: wxid }, { silent: true })
  }, [])

  const flushPendingSelectedWxid = useCallback(async () => {
    const wxid = pendingSelectedWxidRef.current
    if (!wxid) return
    clearPendingSelectedWxidSave()
    await saveSelectedWxidNow(wxid)
  }, [clearPendingSelectedWxidSave, saveSelectedWxidNow])

  const scheduleSelectedWxidSave = useCallback(
    (wxid: string) => {
      if (!wxid) return
      pendingSelectedWxidRef.current = wxid
      if (selectedWxidSaveTimerRef.current) {
        window.clearTimeout(selectedWxidSaveTimerRef.current)
      }
      selectedWxidSaveTimerRef.current = window.setTimeout(() => {
        const pendingWxid = pendingSelectedWxidRef.current
        selectedWxidSaveTimerRef.current = null
        pendingSelectedWxidRef.current = null
        if (pendingWxid) void saveSelectedWxidNow(pendingWxid)
      }, SELECTED_WXID_SAVE_DELAY_MS)
    },
    [saveSelectedWxidNow],
  )

  const persistPendingSelectedWxidBeforePageExit = useCallback(() => {
    const wxid = pendingSelectedWxidRef.current
    if (!wxid) return
    clearPendingSelectedWxidSave()
    const body = JSON.stringify({ lastSelectedWxid: wxid })
    const url = '/api/auth/preferences'
    if (navigator.sendBeacon) {
      const payload = new Blob([body], { type: 'application/json' })
      navigator.sendBeacon(url, payload)
      return
    }
    void fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
    }).catch(() => undefined)
  }, [clearPendingSelectedWxidSave])

  const applyStoredAccount = useCallback((next: LocalWechatAccount) => {
    const normalized: Account = {
      wxid: next.wxid,
      nickName: next.nickName || next.wxid,
      alias: next.alias || undefined,
      avatar: next.avatar || undefined,
    }
    saveStoredAccount(normalized)
    setAccount(normalized)
  }, [])

  const switchAccount = useCallback(
    (next: LocalWechatAccount) => {
      if (next.localUserUuid !== consoleUser.uuid || !assertOwnWechatAccount(next.wxid)) return
      applyStoredAccount(next)
    },
    [applyStoredAccount, assertOwnWechatAccount, consoleUser.uuid],
  )

  const {
    accountProfile,
    profileRefreshing,
    refreshProfile,
    reset: resetAccountProfile,
  } = useAccountProfile(account, setAccount)
  const refreshProfileAndLocalAccounts = useCallback(
    async (currentAccount?: Account | null) => {
      await refreshProfile(currentAccount)
      await refreshLocalAccounts()
    },
    [refreshLocalAccounts, refreshProfile],
  )
  const {
    qrImage,
    loginStatus,
    secureVerifyUrl,
    loginLoading,
    captcha,
    captchaSubmitting,
    refreshQr,
    submitCaptcha,
    resetLogin,
  } = useLogin(account, setAccount, {
    onProfileRefresh: refreshProfileAndLocalAccounts,
  })
  const { contacts, syncing, syncError, syncContacts, resolveContact, reset: resetContacts } = useContactsSync(account)
  const { rules, tasks, settings, saveRule, deleteRule, saveTask, deleteTask, refreshTasks, saveSettings } =
    useBotData(account)
  const { activeMenu, activeItem, activeConversationWxid, groups, friends } = useRouteContext(contacts)
  const { labels } = useLabels(account)
  const openConversation = useCallback(
    (wxid: string) => {
      router.push(`/conversation/realtime?conversationWxid=${encodeURIComponent(wxid)}`)
    },
    [router],
  )
  const { unreadCounts, totalUnread, markConversationRead } = useMessageNotifications({
    account,
    contacts,
    messageNotifications: settings.system.messageNotifications,
    activeConversationWxid,
    conversationPageActive: activeMenu === 'conversation' && activeItem.id === 'realtime',
    onOpenConversation: openConversation,
  })

  const isLoggedIn = !!account

  // 未读数只在进入控制台时拉一次，不做轮询。
  useEffect(() => {
    let cancelled = false
    apiGet<AnnouncementUnreadCountResponse>('/announcements/unread-count', { silent: true })
      .then((response) => {
        if (!cancelled) setAnnouncementUnreadCount(response.count || 0)
      })
      .catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [])

  function clearLocalSession() {
    clearPendingSelectedWxidSave()
    saveStoredAccount(null)
    setAccount(null)
    resetAccountProfile()
    resetContacts()
    router.push('/dashboard')
  }

  function startAddWechatAccount() {
    clearPendingSelectedWxidSave()
    saveStoredAccount(null)
    setAccount(null)
    resetAccountProfile()
    resetContacts()
    router.push('/settings/personal')
  }

  async function consoleLogout() {
    setConsoleLogoutConfirmOpen(true)
  }

  async function confirmConsoleLogout() {
    if (consoleLogoutLoading) return
    setConsoleLogoutLoading(true)
    try {
      await flushPendingSelectedWxid().catch(() => undefined)
      await apiPost('/auth/logout', {}, { silent: true })
    } finally {
      clearPendingSelectedWxidSave()
      saveStoredAccount(null)
      // Drop cross-session local pointers/counters on console logout.
      clearStoredConsoleUserData()
      setAccount(null)
      resetAccountProfile()
      resetContacts()
      setConsoleLogoutLoading(false)
      setConsoleLogoutConfirmOpen(false)
      onConsoleUserChange(null)
    }
  }

  async function logout() {
    const wxid = account?.wxid
    try {
      if (wxid) {
        await apiPost(`/login/LogOut?wxid=${encodeURIComponent(wxid)}`)
      }
    } catch {
      // requestJson already shows the upstream error. Local state still
      // needs to be cleared so the user can return to the login screen.
    } finally {
      clearLocalSession()
    }
  }

  useEffect(() => {
    const wxid = account?.wxid || ''
    if (lastObservedAccountWxidRef.current === wxid) return
    lastObservedAccountWxidRef.current = wxid
    if (!wxid) return
    if (skipNextAccountSaveRef.current) {
      skipNextAccountSaveRef.current = false
      return
    }
    scheduleSelectedWxidSave(wxid)
  }, [account?.wxid, scheduleSelectedWxidSave])

  useEffect(() => {
    if (!localAccountsLoaded || restoredRemoteSelectionRef.current) return
    // /accounts 失败时列表为空是假象：此时消费一次性恢复会误清本地候选。
    if (localAccountsError) return
    restoredRemoteSelectionRef.current = true
    const candidate = storedAccountCandidateRef.current
    storedAccountCandidateRef.current = null
    // Remote preference wins over the local candidate.
    const lastSelectedWxid = consoleUser.lastSelectedWxid || ''
    if (lastSelectedWxid) {
      const selected = localAccounts.find(
        (item) => item.wxid === lastSelectedWxid && item.localUserUuid === consoleUser.uuid,
      )
      if (selected && selected.wxid !== account?.wxid) {
        skipNextAccountSaveRef.current = true
        applyStoredAccount(selected)
        return
      }
    }
    // Use the local candidate only after /accounts confirms current-user ownership.
    if (candidate) {
      const owned = localAccounts.find(
        (item) => item.wxid === candidate.wxid && item.localUserUuid === consoleUser.uuid,
      )
      if (owned) {
        skipNextAccountSaveRef.current = true
        applyStoredAccount(owned)
      } else {
        saveStoredAccount(null)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    localAccountsLoaded,
    localAccountsError,
    localAccounts,
    consoleUser.lastSelectedWxid,
    account?.wxid,
    applyStoredAccount,
  ])

  useEffect(() => {
    window.addEventListener('pagehide', persistPendingSelectedWxidBeforePageExit)
    window.addEventListener('beforeunload', persistPendingSelectedWxidBeforePageExit)
    return () => {
      window.removeEventListener('pagehide', persistPendingSelectedWxidBeforePageExit)
      window.removeEventListener('beforeunload', persistPendingSelectedWxidBeforePageExit)
      clearPendingSelectedWxidSave()
    }
  }, [clearPendingSelectedWxidSave, persistPendingSelectedWxidBeforePageExit])

  useEffect(() => {
    const onSessionExpired = () => {
      if (!account) return
      clearLocalSession()
    }
    window.addEventListener(SESSION_EXPIRED_EVENT, onSessionExpired)
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onSessionExpired)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account])

  useEffect(() => {
    if (isLoggedIn) return
    const allowedLoggedOutItems = menuItemsFor('dashboard', false, consoleUser.role).map((item) => item.id)
    if (activeMenu !== 'dashboard' || !allowedLoggedOutItems.includes(activeItem.id)) {
      router.push('/dashboard')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, activeMenu, activeItem.id])

  return (
    <div className="flex h-screen w-screen overflow-hidden font-sans">
      <PrimaryRail
        avatarUrl={account?.avatar || avatarFor(account?.wxid || 'bot')}
        activeMenu={activeMenu}
        isLoggedIn={isLoggedIn}
        conversationUnreadCount={totalUnread}
        onNavigate={(path) => router.push(path)}
      />
      <SecondaryColumn
        activeMenu={activeMenu}
        activeItemId={activeItem.id}
        isLoggedIn={isLoggedIn}
        consoleUserRole={consoleUser.role}
        badges={{ announcements: announcementUnreadCount }}
        onNavigate={(path) => router.push(path)}
      >
        {activeMenu === 'conversation' && account ? (
          <ChatList
            contacts={contacts}
            account={account}
            activeConversationWxid={activeConversationWxid}
            unreadCounts={unreadCounts}
            onConversationRead={markConversationRead}
            onSelect={openConversation}
          />
        ) : null}
      </SecondaryColumn>
      <MainPane>
        <ViewRouter
          activeItemId={activeItem.id}
          account={account}
          contacts={contacts}
          friends={friends}
          groups={groups}
          labels={labels}
          rules={rules}
          tasks={tasks}
          settings={settings}
          accountProfile={accountProfile}
          localAccounts={localAccounts}
          localAccountsError={localAccountsError}
          licenseStatus={licenseStatus}
          refreshLicenseStatus={refreshLicenseStatus}
          switchAccount={switchAccount}
          removeWxAccount={removeWxAccount}
          onlineWxAccount={onlineWxAccount}
          downlineWxAccount={downlineWxAccount}
          startAddWechatAccount={startAddWechatAccount}
          consoleUser={consoleUser}
          onConsoleUserChange={onConsoleUserChange}
          consoleLogout={consoleLogout}
          announcementUnreadCount={announcementUnreadCount}
          onAnnouncementUnreadCountChange={setAnnouncementUnreadCount}
          syncing={syncing}
          syncError={syncError}
          profileRefreshing={profileRefreshing}
          qrImage={qrImage}
          loginStatus={loginStatus}
          secureVerifyUrl={secureVerifyUrl}
          loginLoading={loginLoading}
          captcha={captcha}
          captchaSubmitting={captchaSubmitting}
          onRefreshQr={refreshQr}
          onSubmitCaptcha={submitCaptcha}
          onResetLogin={resetLogin}
          saveRule={saveRule}
          deleteRule={deleteRule}
          saveTask={saveTask}
          deleteTask={deleteTask}
          refreshTasks={refreshTasks}
          saveSettings={saveSettings}
          syncContacts={syncContacts}
          resolveContact={resolveContact}
          refreshProfile={() => refreshProfileAndLocalAccounts()}
          logout={logout}
        />
      </MainPane>
      {consoleLogoutConfirmOpen ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="console-logout-confirm-title"
          className="fixed inset-0 z-overlay bg-black/35 flex items-center justify-center px-4"
          onClick={() => {
            if (!consoleLogoutLoading) setConsoleLogoutConfirmOpen(false)
          }}
        >
          <div
            className="w-full max-w-md rounded-2xl border border-hair bg-white p-6 shadow-card"
            onClick={(event) => event.stopPropagation()}
          >
            <h3 id="console-logout-confirm-title" className="text-base font-semibold text-ink">
              确认退出控制台？
            </h3>
            <p className="mt-3 text-sm leading-6 text-soft">
              退出后会清除当前控制台会话，并返回控制台登录页；当前页面中的本地微信账号登录态也会清除。
            </p>
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                className="btn-secondary"
                disabled={consoleLogoutLoading}
                onClick={() => setConsoleLogoutConfirmOpen(false)}
              >
                取消
              </button>
              <button
                type="button"
                className="inline-flex items-center justify-center rounded px-5 py-2 text-sm font-medium text-white bg-red-500 hover:bg-red-600 disabled:cursor-not-allowed disabled:opacity-60"
                disabled={consoleLogoutLoading}
                onClick={() => void confirmConsoleLogout()}
              >
                {consoleLogoutLoading ? '退出中...' : '确认退出'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}
